/**
 * 公開アプリ設定サービス
 * 来場者画面で利用する軽微な表示設定を取得します。
 */

import { getSupabaseClient } from '../../services/supabase/client';
import {
  DEFAULT_PUBLIC_APP_SETTINGS,
  mapPublicAppSettingsRow,
} from '../config/appPublicSettings';
import { AppError } from '../utils/errorHandling';
import { logError } from '../utils/logger';

/** 公開設定取得RPC名 */
const GET_PUBLIC_APP_SETTINGS_RPC_NAME = 'get_public_app_settings';

/** 勇者ランク画像公開bucket */
const HERO_RANK_IMAGE_BUCKET_NAME = 'hero-rank-images';

/** 公開設定取得失敗エラーコード */
export const PUBLIC_APP_SETTINGS_FETCH_FAILED_CODE = 'PUBLIC_APP_SETTINGS_FETCH_FAILED';

/**
 * RPC結果から単一行データを取り出す
 * @template T
 * @param {T[] | T | null} rpcData - RPC戻り値
 * @returns {T | null} 単一行データ
 */
const pickSingleRpcRow = (rpcData) => {
  if (Array.isArray(rpcData)) {
    return rpcData[0] ?? null;
  }

  return rpcData ?? null;
};

/**
 * 公開アプリ設定を取得する
 * @returns {Promise<typeof DEFAULT_PUBLIC_APP_SETTINGS>} 公開アプリ設定
 * @throws {AppError} 取得に失敗した場合
 */
export const publicGetAppSettings = async () => {
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(GET_PUBLIC_APP_SETTINGS_RPC_NAME);

  if (error) {
    logError(`${GET_PUBLIC_APP_SETTINGS_RPC_NAME} RPC 実行に失敗しました。`, {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('公開アプリ設定の取得に失敗しました。', {
      code: PUBLIC_APP_SETTINGS_FETCH_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  /** 単一行データ */
  const row = pickSingleRpcRow(data);

  if (!row) {
    return DEFAULT_PUBLIC_APP_SETTINGS;
  }

  /** DB行を検証した公開設定 */
  const mappedSettings = mapPublicAppSettingsRow(row);

  return {
    ...mappedSettings,
    heroRanks: mappedSettings.heroRanks.map((heroRank) => {
      if (!heroRank.imagePath) {
        return heroRank;
      }

      /** 公開Storage URL */
      const publicImageUrl = supabaseClient.storage
        ?.from(HERO_RANK_IMAGE_BUCKET_NAME)
        .getPublicUrl(heroRank.imagePath)
        .data?.publicUrl;

      return {
        ...heroRank,
        imagePath: null,
        imageUrl: typeof publicImageUrl === 'string' && publicImageUrl.trim()
          ? publicImageUrl
          : null,
      };
    }),
  };
};
